import { useEffect, useMemo, useContext } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchTrendingDestinations } from 'actions/DestinationAlertsActions'
import GeoContext from 'contexts/geoContext'
import { trendingHotelDestinations } from 'home/components/TrendingDestinations/constants'
import { escapeRegex } from 'lib/string/regexUtils'

/**
 * Extracts the parenthetical alias from a name, e.g. "Denpasar (Bali)" → "bali"
 */
function extractParenthetical(name: string): string | undefined {
  const match = name.match(/\(([^)]+)\)/)
  return match ? match[1].trim().toLowerCase() : undefined
}

/**
 * Checks if two destination names are a reasonable match.
 * Uses word-boundary matching to avoid false positives
 * (e.g. "Male" should not match "Female" or "Salem").
 */
function namesMatch(a: string, b: string): boolean {
  const na = a.trim().toLowerCase()
  const nb = b.trim().toLowerCase()
  if (!na || !nb) return false
  if (na === nb) return true
  const shorter = na.length <= nb.length ? na : nb
  const longer = na.length > nb.length ? na : nb
  return new RegExp(`\\b${escapeRegex(shorter)}\\b`).test(longer)
}

/**
 * Fetches trending destinations (which carry imageIds) and cross-matches
 * them against a list of popular airport destinations by city/country name.
 *
 * Returns a map of airportCode → imageId.
 */
function useDestinationImages(
  destinations: Array<App.AirportLocation>,
): Record<string, string | undefined> {
  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)

  useEffect(() => {
    dispatch(fetchTrendingDestinations())
  }, [dispatch])

  const apiTrending = useAppSelector(state => state.destination?.trendingDestinations)

  const hardcodedTrending = useMemo(
    () => trendingHotelDestinations[currentRegionCode] ?? trendingHotelDestinations.AU ?? [],
    [currentRegionCode],
  )

  return useMemo(() => {
    const imageMap: Record<string, string | undefined> = {}

    // Build a combined lookup from both API and hardcoded trending destinations
    const trendingEntries: Array<{ name: string; imageId?: string }> = []

    if (apiTrending && apiTrending.length > 0) {
      for (const t of apiTrending) {
        if (t.imageId) {
          trendingEntries.push({ name: t.primaryText, imageId: t.imageId })
        }
      }
    }

    for (const t of hardcodedTrending) {
      if (t.imageId) {
        trendingEntries.push({ name: t.name, imageId: t.imageId })
      }
    }

    if (trendingEntries.length === 0) return imageMap

    for (const dest of destinations) {
      // Collect all possible name candidates from the airport data
      const candidates: Array<string> = []

      if (dest.cityDesc) {
        candidates.push(dest.cityDesc)
        // Also extract alias from parenthetical, e.g. "Denpasar (Bali)" → "Bali"
        const alias = extractParenthetical(dest.cityDesc)
        if (alias) candidates.push(alias)
      }
      if (dest.airportName) candidates.push(dest.airportName)
      if (dest.cityAirportName) candidates.push(dest.cityAirportName)
      if (dest.country) candidates.push(dest.country)

      let matchedImageId: string | undefined

      for (const candidate of candidates) {
        for (const trending of trendingEntries) {
          if (namesMatch(candidate, trending.name)) {
            matchedImageId = trending.imageId
            break
          }
        }
        if (matchedImageId) break
      }

      imageMap[dest.airportCode] = matchedImageId
    }

    return imageMap
  }, [destinations, apiTrending, hardcodedTrending])
}

export default useDestinationImages
